import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Loader2, Sparkles, ArrowRight } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { useFlow } from "../context/FlowContext";
import { API } from "../api";

const sid = () => "chat-" + Math.random().toString(36).slice(2, 10);

export const ChatWidget = () => {
  const { t, lang } = useLang();
  const { openClient } = useFlow();
  const c = t.ai;
  const [open, setOpen] = useState(false);
  const [session] = useState(sid);
  const [messages, setMessages] = useState([{ role: "assistant", content: c.chatIntro }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const startRequest = () => { setOpen(false); openClient(); };

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/ai/chat/stream`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: session, message: text, lang }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += dec.decode(value, { stream: true });
        setMessages((m) => { const copy = [...m]; copy[copy.length - 1] = { role: "assistant", content: acc }; return copy; });
      }
    } catch (err) {
      setMessages((m) => { const copy = [...m]; copy[copy.length - 1] = { role: "assistant", content: c.error }; return copy; });
    }
    setLoading(false);
  };

  return (
    <>
      <button onClick={() => setOpen((o) => !o)} className="fixed bottom-5 right-5 z-[90] w-14 h-14 rounded-full bg-forest text-white shadow-[0_12px_30px_rgb(3,33,42,0.25)] flex items-center justify-center hover:bg-sage-deep transition-all hover:-translate-y-0.5" data-testid="chat-toggle" aria-label="Asistente">
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.98 }} transition={{ duration: 0.25 }}
            className="fixed bottom-24 right-5 z-[90] w-[92vw] max-w-sm h-[70vh] max-h-[560px] bg-white rounded-3xl border border-line shadow-2xl flex flex-col overflow-hidden" data-testid="chat-panel" data-lenis-prevent>
            <div className="bg-forest text-white px-5 py-4 flex items-center gap-2">
              <Sparkles size={18} /> <span className="font-heading font-semibold">{c.chatTitle}</span>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-forest text-white" : "bg-cream text-ink border border-line"}`}>
                    {m.content || (m.role === "assistant" ? <Loader2 size={16} className="animate-spin text-forest" /> : "")}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <button onClick={startRequest} className="mx-3 mb-1 mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-forest text-white font-semibold px-4 py-2.5 text-sm hover:bg-sage-deep transition-all" data-testid="chat-start-request">
              {t.nav.help} <ArrowRight size={15} />
            </button>
            <form onSubmit={send} className="p-3 border-t border-line flex items-center gap-2">
              <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={c.chatPlaceholder} className="flex-1 rounded-full border border-line bg-cream/50 px-4 py-2.5 outline-none focus:border-forest text-sm" data-testid="chat-input" />
              <button type="submit" disabled={loading} className="w-10 h-10 rounded-full bg-forest text-white flex items-center justify-center hover:bg-sage-deep disabled:opacity-50" data-testid="chat-send"><Send size={16} /></button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
